import { useState, useEffect } from "react";
import { X, AlertCircle, Search } from "lucide-react";
import debitNoteService from "../services/debitNoteService";
import saleService from "../services/saleService";
import Swal from "sweetalert2";

export default function CreateDebitNoteModal({ onClose, onSuccess }) {
  const [sales, setSales] = useState([]);
  const [selectedSale, setSelectedSale] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [monto, setMonto] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingSales, setLoadingSales] = useState(true);
  const [searchSale, setSearchSale] = useState("");

  useEffect(() => {
    const fetchSales = async () => {
      try {
        setLoadingSales(true);
        const res = await saleService.getEligibleForDebitNote(searchSale);
        const data = res.data || res;
        setSales(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error cargando ventas:", error);
        Swal.fire("Error", "No se pudieron cargar las ventas", "error");
      } finally {
        setLoadingSales(false);
      }
    };

    const timeout = setTimeout(() => { fetchSales(); }, 300);
    return () => clearTimeout(timeout);
  }, [searchSale]);

  const handleSelectSale = (sale) => setSelectedSale(sale);

  const validateForm = () => {
    if (!selectedSale) {
      Swal.fire("Error", "Debe seleccionar una venta", "warning");
      return false;
    }
    if (!motivo.trim() || motivo.trim().length < 5) {
      Swal.fire("Error", "El motivo debe tener al menos 5 caracteres", "warning");
      return false;
    }
    if (!monto || parseFloat(monto) <= 0) {
      Swal.fire("Error", "Debe ingresar un monto válido", "warning");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const confirm = await Swal.fire({
      title: "¿Crear Nota de Débito?",
      html: `
        <div style="text-align: left;">
          <p><strong>Cliente:</strong> ${selectedSale.client?.nombre || "N/A"}</p>
          <p style="margin-bottom: 4px;"><strong>Código Factura:</strong></p>
          <p style="font-size: 11px; font-family: monospace; word-break: break-all; color: #2563eb; margin-top: 0; padding: 0 10px;">${selectedSale.invoice?.codigo_generacion || "N/A"}</p>
          <p style="margin-top: 12px;"><strong>Monto a aumentar:</strong> $${Number(monto).toFixed(2)}</p>
          <p><strong>Motivo:</strong> ${motivo}</p>
        </div>
        <div style="margin-top: 16px; padding: 12px; background: #fef3c7; border-radius: 8px; border-left: 4px solid #f59e0b;">
          <p style="margin: 0; font-size: 13px; color: #92400e;">
            <strong>⚠️ Importante:</strong> Esta acción generará un nuevo DTE tipo 06 en el Ministerio de Hacienda.
          </p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, crear",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) return;

    setIsSubmitting(true);

    try {
      const payload = {
        sale_id: selectedSale.id,
        motivo: motivo.trim(),
        monto: parseFloat(monto),
      };

      const response = await debitNoteService.create(payload);

      Swal.fire({
        icon: "success",
        title: "¡Nota de Débito Creada!",
        html: `
          <p>La nota de débito se ha emitido correctamente.</p>
          <p style="margin-top: 12px; margin-bottom: 4px;"><strong>Código de Generación:</strong></p>
          <p style="font-size: 11px; color: #2563eb; font-weight: 600; font-family: monospace; word-break: break-all; padding: 0 20px; margin-top: 0;">${response.debit_note?.codigo_generacion || response.codigo_generacion}</p>
        `,
        confirmButtonText: "Aceptar",
      });

      onSuccess();
    } catch (error) {
      console.error("Error creando nota de débito:", error);
      Swal.fire("Error", error.response?.data?.error || error.message || "No se pudo crear la nota de débito", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content card" style={{ maxHeight: "90vh" }}>
            <div className="card-header">
              <h3 className="card-title" style={{ margin: 0 }}>📄 Nueva Nota de Débito (DTE 06)</h3>
              <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={isSubmitting}>
                <X size={18} />
              </button>
            </div>

            <div className="card-body" style={{ overflowY: "auto" }}>
              <form onSubmit={handleSubmit}>
                <div className="alert alert-info" style={{ marginBottom: "20px", display: "flex", gap: "12px", alignItems: "start" }}>
                  <AlertCircle size={20} style={{ marginTop: "2px", flexShrink: 0 }} />
                  <div style={{ fontSize: "14px" }}>
                    <strong>¿Qué es una Nota de Débito?</strong>
                    <p style={{ margin: "8px 0 0 0" }}>
                      Es un DTE tipo 06 que se genera para AUMENTAR el valor de un Crédito Fiscal ya emitido
                      (ej: ajustes de precio, cargos adicionales, intereses).
                    </p>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label"><strong>1. Seleccionar Venta/Factura (Crédito Fiscal)</strong></label>

                  <div style={{ position: "relative", marginBottom: "12px" }}>
                    <Search size={18} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#a0aec0" }} />
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Buscar por cliente..."
                      value={searchSale}
                      onChange={(e) => setSearchSale(e.target.value)}
                      style={{ paddingLeft: "40px" }}
                    />
                  </div>

                  <div style={{ maxHeight: "300px", overflowY: "auto", border: "2px solid #e2e8f0", borderRadius: "8px", background: "#f7fafc" }}>
                    {loadingSales ? (
                      <div className="text-center" style={{ padding: "40px" }}>
                        <div className="spinner" style={{ margin: "0 auto" }}></div>
                        <p style={{ marginTop: "16px", color: "#718096", fontSize: "14px" }}>Cargando ventas...</p>
                      </div>
                    ) : sales.length === 0 ? (
                      <div className="text-center" style={{ padding: "40px" }}>
                        <p style={{ color: "#a0aec0", fontSize: "14px", margin: 0 }}>
                          {searchSale ? "No se encontraron ventas con ese criterio" : "No hay ventas elegibles para nota de débito"}
                        </p>
                        <p style={{ color: "#718096", fontSize: "12px", marginTop: "8px" }}>
                          Solo se muestran facturas de Crédito Fiscal (CCF) validadas
                        </p>
                      </div>
                    ) : (
                      sales.map((sale) => (
                        <div
                          key={sale.id}
                          onClick={() => handleSelectSale(sale)}
                          style={{
                            padding: "12px",
                            borderBottom: "1px solid #e2e8f0",
                            cursor: "pointer",
                            background: selectedSale?.id === sale.id ? "#dbeafe" : "white",
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, color: "#1f2937", marginBottom: "4px" }}>
                                {sale.client?.nombre || "Cliente desconocido"}
                              </div>
                              <div style={{ fontSize: "10px", color: "#6b7280", marginBottom: "4px", fontFamily: "monospace", wordBreak: "break-all", maxWidth: "400px" }}>
                                <strong>Código:</strong> {sale.invoice?.codigo_generacion || "N/A"}
                              </div>
                              <div style={{ fontSize: "12px", color: "#9ca3af" }}>
                                Fecha: {new Date(sale.fecha_venta).toLocaleDateString("es-SV")}
                              </div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: "18px", fontWeight: 700, color: "#2563eb" }}>
                                ${Number(sale.monto_final).toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {selectedSale && (
                    <div style={{ marginTop: "12px", padding: "12px", background: "#eff6ff", border: "2px solid #3b82f6", borderRadius: "8px" }}>
                      <strong style={{ color: "#1e40af" }}>✓ Venta seleccionada:</strong>
                      <div style={{ fontSize: "14px", color: "#1e40af", marginTop: "4px" }}>
                        {selectedSale.client?.nombre} - ${Number(selectedSale.monto_final).toFixed(2)}
                      </div>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label"><strong>2. Monto a Aumentar</strong></label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    className="form-control"
                    placeholder="Ej: 10.00"
                    value={monto}
                    onChange={(e) => setMonto(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label"><strong>3. Motivo de la Nota de Débito</strong></label>
                  <textarea
                    className="form-control"
                    rows="4"
                    placeholder="Ej: Ajuste de precio por..."
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    maxLength={500}
                    required
                  />
                  <small style={{ color: "#718096", fontSize: "12px" }}>{motivo.length}/500 caracteres</small>
                </div>
              </form>
            </div>

            <div className="card-body" style={{ borderTop: "2px solid #e2e8f0", display: "flex", gap: "12px", paddingTop: "var(--spacing-md)" }}>
              <button className="btn btn-secondary" onClick={onClose} disabled={isSubmitting} style={{ flex: 1 }}>
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={isSubmitting || !selectedSale || !motivo.trim() || !monto}
                style={{ flex: 1 }}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Procesando...
                  </>
                ) : (
                  "✓ Crear Nota de Débito"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}